import { type HlsConfig } from 'hls.js';
import { isWaitable, PlaybackError } from '../core/errors';
import { baseUrlForEnv, type HavikEnv } from '../core/env';
import { resolvePlaybackOnce, resolveStream, type ResolveOptions } from '../core/resolve';
import { HlsEngine, hlsSupported } from '../core/engine/hls';
import { NativeHlsEngine, nativeHlsSupported, webkitMediaStack } from '../core/engine/native';
import { type EngineEvent, type PlaybackEngine } from '../core/engine/types';
import {
  attachMediaStateListeners,
  EMPTY_STATS,
  type AudioTrackInfo,
  type PlaybackStats,
  type PlayerState,
  type QualityLevel,
  type TextTrackInfo,
} from '../core/stats';
import { getDeviceId } from '../core/drm/deviceId';
import { enterVideoFullscreen, exitVideoFullscreen } from '../core/fullscreen';
import {
  resolveCredential,
  type Credential,
  type CredentialSource,
  type StreamDescriptor,
} from '../core/types';
import { type WaitForLive, type WaitForLiveOptions, type WaitState } from '../core/waitForLive';
import {
  subscribeLiveState,
  deriveEventsBaseUrl,
  type EndedReason,
  type LiveStateSubscription,
} from '../core/events';
import { AnalyticsSession, resolveBeaconsEndpoint, type Presentation } from '../core/analytics';
import { collectEnvironment, collectHighEntropy } from '../core/environment';

// Re-exported: it names the `ended` event payload and Player.endedReason, so
// a managed-player consumer must be able to refer to it without reaching into
// the BYO entry point.
export type { EndedReason } from '../core/events';
import { SDK_VERSION } from '../core/version';
import { applyTheme, type HavikTheme } from '../ui/theme';
import { mountControlBar, type ControlBarHandle } from '../ui/controls';

// Armed-phase SSE tuning. While SSE is healthy the player does NOT poll
// /v1/playback — it waits for the pushed `live`. These bound the fallback:
const SSE_ARM_CONNECT_GRACE_MS = 6_000; // no SSE frame by now → start polling
const SSE_ARM_LIVENESS_MS = 45_000; // heartbeat is 20s; 2 missed → SSE stale → poll
const SSE_ARM_RESOLVE_JITTER_MS = 2_000; // stagger the go-live resolve so 1000s of armed viewers don't resolve in lockstep
const LIVE_STATE_WARN_AFTER = 3; // consecutive no-data failures before the one-time degraded-end-detection warning

export interface CreatePlayerOptions {
  /** The <video> element the SDK will own. */
  video: HTMLVideoElement;
  /**
   * Which Oddin environment to play against. Prefer this over
   * {@link baseUrl}: the host is then correct by construction, and so is
   * everything derived from it (the SSE events host, the QoE beacons host).
   *
   * Exactly one of `env` or {@link baseUrl} is required. Passing both is an
   * error rather than a precedence rule — the two disagreeing is precisely
   * the misconfiguration worth refusing, and silently preferring one would
   * point a player at an environment nobody chose.
   */
  env?: HavikEnv;
  /**
   * API base URL, when {@link env} does not name it. Use this for a
   * deployment the named environments do not cover: your own CDN or gateway
   * fronting the streams API, the CN plane, or a pinned build that must
   * outlive a domain change.
   *
   * Note that a base which is not a `feed[-dev].<domain>` host derives no QoE
   * beacons endpoint, so pair it with `analytics: { endpoint }`.
   */
  baseUrl?: string;
  matchUrn: string;
  credential: CredentialSource;
  /**
   * Start playback once ready. Default: true. With false the player resolves
   * playback, then holds in a pre-play 'paused' state (poster + play card,
   * no spinner) and defers media loading — segments and DRM license traffic —
   * until the first play(). The hls.js engine still parses the manifest
   * pre-play ('ready', track lists); the native engine defers all fetching
   * and emits 'ready' at attach. A policy-blocked programmatic play()
   * ('autoplayblocked') leaves the loader warming the buffer — media and DRM
   * traffic flows from that kick, not from a viewer gesture.
   */
  autoplay?: boolean;
  /** Mute the element (required for reliable autoplay with sound policies). Default: false. */
  muted?: boolean;
  /** Arm on an upcoming match and auto-attach at go-live. See WaitForLive. */
  waitForLive?: WaitForLive;
  /** 'auto' lets hls.js decide from the manifest (safe default). Default: 'auto'. */
  lowLatency?: 'auto' | boolean;
  /** Poster image shown before playback starts. */
  poster?: string;
  /** Initial rendition index (-1 = auto, the default). */
  startLevel?: number;
  /** Cap ABR to renditions at/below this bitrate (bps). */
  maxBitrate?: number;
  /**
   * Target live-edge latency in seconds (maps to hls.js liveSyncDuration).
   * Default: 2 (a balance the engine ships — close to live but back from the
   * bleeding PART-HOLD-BACK edge, where parts aren't reliably published yet).
   * Lower = closer to live but more rebuffer-prone; higher = steadier. The
   * engine derives a matching latency ceiling internally, so this is the only
   * live-latency knob you need. Live/LL only.
   */
  liveLatencyTarget?: number;
  /**
   * Snap straight to the live edge when a backgrounded tab is refocused and
   * playback has fallen well behind. Default: true. Live/LL only.
   */
  snapToLiveOnRefocus?: boolean;
  /** Force an engine; 'auto' prefers hls.js wherever supported (incl. modern
   *  Safari/iOS via ManagedMediaSource) and falls back to native HLS. Default: 'auto'. */
  engine?: 'auto' | 'hls' | 'native';
  /** Optional viewer id (X-User-Id on the license POST). */
  userId?: string;
  /**
   * Which controls to render:
   *  - 'custom' (default): the Oddin-branded, skinnable control bar.
   *  - 'native': the browser's built-in `<video controls>` UI.
   *  - 'none': no controls (drive the player via its API).
   */
  controls?: 'custom' | 'native' | 'none';
  /**
   * Re-skin the custom control bar. Merged over the Oddin default; any omitted
   * key keeps the brand value. Equivalent to overriding the `--havik-*` CSS
   * variables on the player element. Ignored unless controls is 'custom'.
   */
  theme?: Partial<HavikTheme>;
  /**
   * Show branded full-bleed cards for the stopped/pre-play states — ended
   * ("This stream has ended"), fatal error (message + Retry when retryable),
   * pre-play (poster + play CTA), and armed-waiting ("Starting soon…") — instead
   * of a frozen frame + a dead play button. Themed via the control-bar tokens +
   * `theme.logoUrl`. Default: true. Set false if you render your own end/error
   * UI from the player events (`ended`, `error`). Ignored unless controls is
   * 'custom'.
   */
  statusOverlays?: boolean;
  /** Message on the end-of-stream card. Default: "This stream has ended". */
  endedMessage?: string;
  /**
   * Message shown instead of {@link endedMessage} when the stream stopped but
   * the match is still running (the server's `interrupted` reason — an ingest
   * outage that outlived the bridge's reconnect hold). Default:
   * "Reconnecting — please stand by" — a promise the player keeps while
   * {@link rejoinOnLive} is on (the default; it also needs
   * {@link liveStateEvents}). Override it if you disable auto-rejoin, since
   * nothing will then re-attach on its own.
   */
  interruptedMessage?: string;
  /** Message on the fatal-error card. Default: the error's own message. */
  errorMessage?: string;
  /**
   * Turn on hls.js's internal console logging — segment/part scheduling, ABR
   * switches, and EME key-session + license traffic. Default: false.
   *
   * For diagnosing a playback problem with Oddin support; leave it off in
   * production (it logs on every fragment, and the EME lines include license
   * request/response metadata). hls.js engine only — a no-op on the native
   * engine, which has no equivalent. Equivalent to `hlsConfig: { debug: true }`,
   * which still wins if you set both.
   */
  debug?: boolean;
  /** Escape hatch for low-level hls.js tuning. */
  hlsConfig?: Partial<HlsConfig>;
  /** Stats sampling cadence. Default: 2000ms. */
  statsIntervalMs?: number;
  /**
   * How often to silently re-resolve playback so the signed DRM license URL
   * the player would use next stays inside its signature lifetime (~10 min).
   * Default: 8 min. Set 0 to disable.
   *
   * This refreshes the URL, not the license. It gives the player a newer URL
   * for the next license request it makes, and issues none of its own, so it
   * cannot extend a license the CDM already holds: how long that stays valid
   * is set when it is issued, server-side. A session that outlasts its license
   * ends with a fatal `DRM_CLIENT` error whatever this is set to.
   */
  licenseRefreshMs?: number;
  /**
   * Subscribe to push-based live-state over SSE (`events.<domain>/v1/events/{urn}`)
   * to detect end-of-stream the instant the bridge flips, instead of waiting on
   * the frozen Tencent manifest (which never emits #EXT-X-ENDLIST). On `ended`/
   * `gone` the player stops buffering and emits `ended`; a later pushed `live`
   * rejoins automatically (see {@link rejoinOnLive}). Default: true — and
   * fully graceful: if the endpoint is unreachable the subscription retries
   * quietly in the background while the HLS staleness watchdog remains the
   * layer-fallback, so playback is never affected. Set false to disable.
   */
  liveStateEvents?: boolean;
  /**
   * Rejoin automatically when a stream that ended comes back live. An ingest
   * outage longer than the bridge's reconnect grace tears the session down
   * and — when the encoder reconnects — recreates it under a NEW manifest
   * URL, while the old playlist freezes at HTTP 200 forever. A player that
   * latched `ended` can therefore only recover by re-resolving playback.
   * With this on (the default), the live-state subscription stays open after
   * `ended` (it closes on `gone`, the past-catchup terminal), and a pushed
   * `live` re-arms and re-attaches automatically through the same jittered
   * go-live path the pre-live wait uses. Set false to keep `ended` terminal
   * (the pre-1.13 behavior). No effect when {@link liveStateEvents} is
   * disabled — without the push channel there is nothing to rejoin on.
   */
  rejoinOnLive?: boolean;
  /**
   * Override the SSE endpoint base URL. Default: derived from {@link baseUrl} by
   * prefixing the host with `events.` (e.g. `feed.<domain>` →
   * `events.<domain>`, served ALB-direct). Set this if your events subdomain
   * differs from that convention.
   */
  eventsBaseUrl?: string;
  /**
   * Extra headers attached to every request the SDK makes to {@link baseUrl}
   * (playback resolution, live-status polling) — and ONLY there: never to
   * CDN manifest/segment hosts, DRM license endpoints, the SSE events host,
   * or the beacons endpoint. For integrations whose API front door wants
   * its own auth signal on top of the publishable key — an operator console
   * proving a session to its reverse proxy, or a customer gateway in front
   * of the streams API. `x-api-key` cannot be overridden through this map.
   */
  apiHeaders?: Record<string, string>;
  /**
   * QoE beacons + CMCD session attribution. Active only when the playback
   * response carries an analytics sid (the server-side flag) — without one
   * this option does nothing. Default: true, with the beacons endpoint
   * derived from {@link baseUrl} by hostname convention (`feed[-dev].…` →
   * `beacons[-dev].…`). Pass `false` to opt out entirely, or
   * `{ endpoint }` to point at a non-conventional beacons host.
   *
   * If {@link baseUrl} is NOT a `feed[-dev].<domain>` host — a CDN or gateway
   * fronting the streams API, say — nothing can be derived and beacons are
   * disabled. That is reported, not silent: a `console.warn` naming the host
   * (once per host) plus a non-fatal `warning` event. Supply `{ endpoint }`.
   *
   * Privacy: the sid is an opaque per-session id, held in memory only and
   * never persisted. Beacons carry playback QoE numbers plus the session's
   * environment (form factor, device model and OS version where the browser
   * offers client hints, screen, connection, embedding origin) — never an
   * IP, never the raw user agent, and no identifier beyond the sid.
   */
  analytics?:
    | boolean
    | {
        endpoint?: string;
        /**
         * How long the player may sit paused before the session closes itself
         * (`END_REASON_IDLE`), and the suspension gap (OS sleep, a frozen tab)
         * after which it does the same on waking. Default 10 minutes; `0`
         * disables the idle close; the suspension gap never goes below five
         * minutes, whatever this is set to (a hidden tab's throttled timers
         * legitimately gap to a minute). After an idle close the next play()
         * reloads the stream — a fresh session, a fresh sid, the live edge.
         */
        idleMs?: number;
      };
}

export type PlayerEvent =
  | 'ready'
  | 'waiting'
  | 'playing'
  | 'buffering'
  | 'paused'
  | 'ended'
  | 'error'
  | 'warning'
  | 'autoplayblocked'
  | 'qualitychange'
  | 'audiotrackchange'
  | 'texttrackchange'
  | 'pipchange'
  | 'stats'
  | 'statechange';

export interface PlayerEventMap {
  ready: void;
  waiting: WaitState;
  playing: void;
  buffering: void;
  paused: void;
  /**
   * End of stream. The payload says WHY when the server told us
   * (`interrupted` = the match is still running and play should resume, so
   * do NOT say "ended"; `match_ended` = a real end); `undefined` means the
   * end came from the HLS staleness watchdog or a server predating the
   * field — treat it as a real end, the pre-existing behavior. Fires again
   * with `match_ended` if an interruption later turns out to be the end.
   */
  ended: EndedReason | undefined;
  error: PlaybackError;
  /** Non-fatal condition (e.g. FairPlay-not-implemented passthrough, a failed license refresh). Playback continues. */
  warning: PlaybackError;
  /** The browser blocked autoplay (NotAllowedError). Prompt a tap-to-play / tap-to-unmute affordance. */
  autoplayblocked: void;
  /** Active rendition changed; payload is the level index (-1 = auto). */
  qualitychange: number;
  /** Active audio track changed; payload is the track id. */
  audiotrackchange: number;
  /** Active text track changed; payload is the track id (-1 = off). */
  texttrackchange: number;
  /** Picture-in-Picture entered (true) or exited (false). */
  pipchange: boolean;
  stats: PlaybackStats;
  statechange: PlayerState;
}

export interface Player {
  readonly state: PlayerState;
  readonly descriptor: StreamDescriptor | null;
  /** Why the stream ended, when the server said so; null otherwise. Cleared
   *  when fresh playback commits (including a rejoin). See EndedReason. */
  readonly endedReason: EndedReason | null;
  play(): Promise<void>;
  pause(): void;
  setMuted(muted: boolean): void;
  setVolume(volume: number): void;
  getStats(): PlaybackStats;
  /** Available renditions (empty on the native engine, which does ABR internally). */
  getQualityLevels(): QualityLevel[];
  /** Current rendition index, or -1 when ABR is choosing automatically. */
  getCurrentQuality(): number;
  /** Pick a rendition by index, or 'auto'/-1 to re-enable ABR. */
  setQuality(index: number | 'auto'): void;
  /** Cap ABR to renditions at/below this bitrate (bps), or null to clear. */
  setMaxBitrate(bitrate: number | null): void;
  getAudioTracks(): AudioTrackInfo[];
  setAudioTrack(id: number): void;
  getTextTracks(): TextTrackInfo[];
  /** Select a text track by id, or -1 to disable captions. */
  setTextTrack(id: number): void;
  /** Jump to the live edge (live streams). */
  seekToLive(): void;
  enterPictureInPicture(): Promise<void>;
  exitPictureInPicture(): Promise<void>;
  /**
   * Enter fullscreen: element fullscreen on the player container where the
   * API exists (custom controls stay on screen); on iPhone Safari falls back
   * to the video-native presentation with NATIVE controls (there is no
   * element Fullscreen API there at all). Call from a user gesture; on
   * iPhone it takes effect once media is loaded. Resolves false when no
   * fullscreen mechanism exists on the platform.
   */
  enterFullscreen(): Promise<boolean>;
  /** Leave fullscreen (element or video-native). Safe when not fullscreen. */
  exitFullscreen(): Promise<void>;
  /** Retry playback after an error (re-resolves and re-attaches). */
  retry(): Promise<void>;
  /** Re-resolve playback (refreshes the signed license URL) and re-attach. */
  reload(): Promise<void>;
  on<E extends PlayerEvent>(event: E, cb: (payload: PlayerEventMap[E]) => void): () => void;
  destroy(): void;
}

const DEFAULT_STATS_INTERVAL_MS = 2000;
/**
 * How often to re-resolve playback so the signed license URL the engine would
 * use is younger than havik-streams' DRM_SIGN_TTL (10m).
 *
 * This refreshes the URL, NOT the license: it hands the engine a new string
 * (see refreshLicense → engine.setLicenseUrl), and licenseXhrSetup reads that
 * string when hls.js next POSTs. It issues no license request of its own, so it
 * cannot extend a license the CDM has already taken — the licence's own
 * expiration governs that, and it is set server-side
 * (havik-drm LICENSE_EXPIRATION_DURATION). Do not "fix" that by rebuilding the
 * instance on a timer: a rebuild is a visible interruption, and the CDM it
 * would want to re-key is not replaceable anyway (see core/drm/cdm.ts).
 */
const DEFAULT_LICENSE_REFRESH_MS = 8 * 60 * 1000;

/**
 * Options with the endpoint decision already made. The player reads
 * `opts.baseUrl` in a dozen places (resolve, SSE derivation, beacons
 * derivation); resolving once at the boundary keeps every one of them a
 * plain string instead of pushing an `env`-or-`baseUrl` branch inward.
 */
type ResolvedPlayerOptions = CreatePlayerOptions & { baseUrl: string };

/**
 * Settle `env` / `baseUrl` into the one base URL the player will use, or
 * throw. Kept separate from the other createPlayer guards because it is the
 * only one that has to reject a combination rather than an absence.
 */
function resolveEndpoint(opts: CreatePlayerOptions): string {
  if (opts.env !== undefined && opts.baseUrl !== undefined) {
    throw new PlaybackError(
      'INTERNAL',
      0,
      `createPlayer: pass env OR baseUrl, not both (got env=${JSON.stringify(opts.env)} ` +
        `and baseUrl=${JSON.stringify(opts.baseUrl)})`,
    );
  }
  if (opts.env !== undefined) return baseUrlForEnv(opts.env);
  if (!opts.baseUrl) {
    throw new PlaybackError(
      'INTERNAL',
      0,
      "createPlayer: env or baseUrl is required (env: 'integration' | 'production')",
    );
  }
  return opts.baseUrl;
}

/**
 * Endpoint selection, enforced at COMPILE time.
 *
 * `env` and `baseUrl` are both optional on {@link CreatePlayerOptions} so it
 * stays an interface others can `extend` and `Partial<>`. That alone would
 * make "neither given" type-check and fail only in the browser, which is a
 * guarantee this SDK used to have while `baseUrl` was required. These two
 * overloads put it back at the call site without changing the interface: one
 * names `env` and forbids `baseUrl`, the other the reverse, so neither and
 * both are both "no overload matches this call".
 *
 * resolveEndpoint still enforces the same rule at runtime — the overloads do
 * nothing for a JavaScript caller, and that is most of the embed surface.
 */
export function createPlayer(
  opts: CreatePlayerOptions & { env: HavikEnv; baseUrl?: never },
): Promise<Player>;
export function createPlayer(
  opts: CreatePlayerOptions & { baseUrl: string; env?: never },
): Promise<Player>;
/**
 * Mode A — managed player. Owns the <video> and a bundled hls.js, wiring
 * LL-HLS + DRM automatically. With `waitForLive`, arms on an upcoming match and
 * auto-plays the instant it goes live. Resolves to a Player as soon as it is
 * armed/attached — terminal errors (404/410/401/...) reject; the live wait does
 * not block and is surfaced via 'waiting' events.
 */
export async function createPlayer(opts: CreatePlayerOptions): Promise<Player> {
  // Fail fast with typed errors on misuse, before any async/DOM work.
  if (!opts.video || typeof (opts.video as HTMLVideoElement).play !== 'function') {
    throw new PlaybackError('INTERNAL', 0, 'createPlayer: opts.video must be an HTMLVideoElement');
  }
  const baseUrl = resolveEndpoint(opts);
  if (!opts.matchUrn)
    throw new PlaybackError('INVALID_URN', 0, 'createPlayer: matchUrn is required');
  if (!opts.credential) {
    throw new PlaybackError('INTERNAL', 0, 'createPlayer: credential is required');
  }
  const player = new ManagedPlayer({ ...opts, baseUrl });
  await player.start();
  return player;
}

class ManagedPlayer implements Player {
  private readonly opts: ResolvedPlayerOptions;
  private readonly abort = new AbortController();
  private readonly listeners: { [E in PlayerEvent]?: Set<(p: never) => void> } = {};
  private engine: PlaybackEngine | null = null;
  private controlBar: ControlBarHandle | null = null;
  private container: HTMLElement | null = null;
  private detachMedia?: () => void;
  private detachPip?: () => void;
  private statsTimer?: ReturnType<typeof setInterval>;
  private refreshTimer?: ReturnType<typeof setInterval>;
  private _state: PlayerState = 'idle';
  private _descriptor: StreamDescriptor | null = null;
  private destroyed = false;
  /** Monotonic load generation; a load whose generation is stale must not commit. */
  private loadGen = 0;
  /** Push-based live-state subscription (SSE), active during committed playback. */
  private liveStateSub: LiveStateSubscription | null = null;
  /** Guards the end-of-stream handler so SSE and the HLS watchdog can't double-fire. */
  private endedHandled = false;
  /** Why the current end happened, when the server said (see EndedReason).
   *  null = ended without a reason (an older server, or the HLS staleness
   *  watchdog getting there first) — callers must read that as "unknown",
   *  which is exactly the pre-#191 behavior: assume a real end. */
  private _endedReason: EndedReason | null = null;
  /** Pending post-ended rejoin (the jitter timer; see scheduleRejoin). */
  private rejoinTimer: ReturnType<typeof setTimeout> | null = null;
  /** Live-state subscription used during the pre-live armed/waiting phase. */
  private armingSub: LiveStateSubscription | null = null;
  /** Connect-grace / heartbeat-liveness timer for the armed SSE subscription. */
  private armSseTimer: ReturnType<typeof setTimeout> | null = null;
  /** Abort handle for the SSE arm's polling fallback — closeArming() (and thus
   *  reload()/retry()/destroy()) must cancel it, or an orphaned poll keeps
   *  hitting /v1/playback and emitting stale 'waiting' for a dead generation. */
  private armFallbackCtrl: AbortController | null = null;
  /** Last fatal error routed through fail() — start() turns a fatal engine
   *  load into a createPlayer rejection with it (the emit happens before the
   *  caller can attach listeners, so the event alone is unobservable there). */
  private _lastError: PlaybackError | null = null;
  /** QoE beacon session (plan A2); one per sid, created at engine commit. */
  private analyticsSession: AnalyticsSession | null = null;
  private detachMediaListeners?: () => void;
  /** True once play() was ever asked for (user gesture or API). Ends the
   *  autoplay-off deferral, and lets a post-play reload/rejoin resume without
   *  a second gesture — playback the viewer started should come back on its
   *  own after a recovery re-attach. */
  private playRequested = false;
  /**
   * The beacon session closed itself for inactivity (see the `idleMs`
   * option): the sid is finished and the engine was stopped, so the next
   * play() is a rejoin — reload() resolves a new sid and re-plays from the
   * live edge — not a resume of a session that no longer exists.
   */
  private idleClosed = false;
  /** A deferLoad engine is attached and waits for the first play() to start
   *  media loading (autoplay-off pre-play). */
  private pendingStartLoad = false;

  constructor(opts: ResolvedPlayerOptions) {
    this.opts = opts;
  }

  get state(): PlayerState {
    return this._state;
  }
  get descriptor(): StreamDescriptor | null {
    return this._descriptor;
  }
  get endedReason(): EndedReason | null {
    return this._endedReason;
  }

  async start(): Promise<void> {
    if (this.opts.muted) this.opts.video.muted = true;
    if (this.opts.poster) this.opts.video.poster = this.opts.poster;
    this.mountControls();
    this.attachPipListeners();
    this.setState('loading');
    const gen = ++this.loadGen;
    const resolveOpts = this.resolveOptions();
    try {
      const descriptor = await resolvePlaybackOnce(resolveOpts);
      await this.onResolved(descriptor, gen);
      if (gen === this.loadGen && this._state === 'error' && this._lastError) {
        // Fatal engine load (already routed through fail()) — reject so the
        // first-load caller observes the typed error; the 'error' event fired
        // before any listener could exist. Unwind FULLY first: the caller
        // never receives a Player to destroy(), so leaving the mounted
        // control bar / PiP listeners / video wrapper behind would leak DOM
        // for the life of the page.
        const err = this._lastError;
        this.destroy();
        throw err;
      }
    } catch (err) {
      if (this.opts.waitForLive && err instanceof PlaybackError && isWaitable(err.code)) {
        // Don't block: return the (armed) player; keep polling in the background.
        this.setState('waiting');
        this.beginArming(gen, err.liveStartsAtMs);
        return;
      }
      throw err;
    }
  }

  private resolveOptions(): ResolveOptions {
    return {
      baseUrl: this.opts.baseUrl,
      matchUrn: this.opts.matchUrn,
      credential: this.opts.credential,
      signal: this.abort.signal,
      apiHeaders: this.opts.apiHeaders,
    };
  }

  private beginArming(gen: number, liveStartsAtMs?: number, reconnect = false): void {
    const base =
      this.opts.liveStateEvents === false
        ? undefined
        : (this.opts.eventsBaseUrl ?? deriveEventsBaseUrl(this.opts.baseUrl));
    if (base) this.armWithSse(gen, base, liveStartsAtMs, reconnect);
    else this.beginArmingByPolling(gen);
  }

  /** SSE-disabled path: the original kickoff-aware /v1/playback poll. */
  private beginArmingByPolling(gen: number): void {
    resolveStream({
      ...this.resolveOptions(),
      signal: this.abort.signal,
      waitForLive: this.buildWaitConfig(gen),
    })
      .then((descriptor) => {
        // Same staleness guard as the catch: a resolve that SETTLED before a
        // supersede (reload/destroy bumps loadGen) must not build an engine.
        if (this.destroyed || this.abort.signal.aborted || gen !== this.loadGen) return;
        return this.onResolved(descriptor, gen);
      })
      .catch((err) => {
        if (this.destroyed || this.abort.signal.aborted || gen !== this.loadGen) return;
        // Wrap non-PlaybackErrors (e.g. a rejecting credential function) so
        // the 'error' event payload honors its documented type.
        this.fail(
          err instanceof PlaybackError ? err : new PlaybackError('INTERNAL', 0, String(err)),
        );
      });
  }

  /**
   * Armed wait, SSE-first. While the live-state stream is healthy the player
   * does NOT poll /v1/playback — it waits for the pushed `live`, then resolves
   * exactly once (jittered, to stagger the go-live herd). Polling only starts
   * if SSE fails to connect within a grace, goes stale (missed heartbeats), or
   * errors — so an unreachable events endpoint is no worse than today.
   */
  private armWithSse(
    gen: number,
    eventsBaseUrl: string,
    liveStartsAtMs?: number,
    reconnect = false,
  ): void {
    let committed = false;
    let fallbackStarted = false;
    const fallbackCtrl = new AbortController();
    // No abort-listener chain to this.abort here: every abandonment path
    // (reload()/retry()/destroy()/commit) runs closeArming(), which aborts
    // this controller — a per-arm listener on the player-lifetime signal
    // would just accumulate one captured closure per re-arm cycle.
    this.armFallbackCtrl = fallbackCtrl;
    this.setState('waiting');
    // Kickoff hint precedence mirrors the polling path: the server's 425
    // liveStartsAt wins, the caller's waitForLive.kickoffAt is the fallback.
    const wf = this.opts.waitForLive;
    const kickoffOpt = wf && wf !== true ? wf.kickoffAt : undefined;
    const kickoffOptMs =
      typeof kickoffOpt === 'number'
        ? kickoffOpt
        : typeof kickoffOpt === 'string'
          ? Date.parse(kickoffOpt)
          : NaN;
    const startsAtMs = liveStartsAtMs ?? (Number.isFinite(kickoffOptMs) ? kickoffOptMs : undefined);
    // Emit an observable 'waiting' for the SSE arm. The polling path emits
    // one per poll via buildWaitConfig, but a healthy SSE wait makes no
    // /v1/playback requests at all — without this, an armed player is
    // invisible to hosts (the pre-resolve statechange is deduped and
    // createPlayer resolves already armed; the ops Demo Player had to probe
    // player.state synchronously to find out). Deferred a MACROTASK: this
    // runs synchronously inside start(), before createPlayer resolves — a
    // synchronous (or microtask) emit would fire before the caller can
    // attach listeners and be missed, recreating the exact gap this closes.
    // Synthetic-but-honest shape: the next self-check is the SSE liveness
    // window.
    // No clearTimeout-on-abort bookkeeping: the callback's committed/
    // destroyed/gen guard makes a post-teardown firing a no-op, and the
    // timer is 0 ms — long gone by any later re-arm. (A per-arm abort
    // listener on the player-lifetime signal would leak one closure per
    // re-arm cycle.)
    setTimeout(() => {
      if (committed || this.destroyed || gen !== this.loadGen) return;
      this.emit('waiting', {
        // A rejoin arm is a mid-match reconnect, not a pre-kickoff wait —
        // 'unavailable' is the phase hosts map to "temporarily unavailable"
        // copy; 'tooEarly' would read as "starting soon" while a live match
        // recovers from an ingest outage.
        phase: reconnect ? 'unavailable' : 'tooEarly',
        retryInMs: SSE_ARM_LIVENESS_MS,
        attempt: 1,
        elapsedMs: 0,
        ...(!reconnect && startsAtMs !== undefined && startsAtMs > Date.now()
          ? { ttkMs: startsAtMs - Date.now() }
          : {}),
      });
    }, 0);

    const stale = (): boolean =>
      this.destroyed || this.abort.signal.aborted || gen !== this.loadGen;

    const commit = async (descriptor: StreamDescriptor): Promise<void> => {
      if (committed || stale()) return;
      committed = true;
      this.closeArming();
      fallbackCtrl.abort(); // supersede any running poll fallback
      try {
        await this.onResolved(descriptor, gen);
      } catch (err) {
        // onResolved can reject (credential mint at go-live, engine
        // construction). By now committed=true, so the callers' .catch
        // handlers early-return — an unhandled rejection here would strand
        // the player in 'waiting' forever with arming already closed.
        // Surface it: listeners exist by now (this is post-createPlayer).
        if (stale()) return;
        this.fail(
          err instanceof PlaybackError ? err : new PlaybackError('INTERNAL', 0, String(err)),
        );
      }
    };

    const startFallback = (): void => {
      if (committed || fallbackStarted || stale()) return;
      fallbackStarted = true;
      // Route the fallback's success through commit(), NOT straight to
      // onResolved: SSE can recover and push 'live' while the poll's resolve
      // has already settled (abort can't retract a settled promise), and two
      // unguarded onResolved calls for the same generation would build two
      // engines on one video — the first leaks its watchdog + media
      // listeners. commit() is idempotent, so whichever side wins, the other
      // becomes a no-op.
      resolveStream({
        ...this.resolveOptions(),
        signal: fallbackCtrl.signal,
        waitForLive: this.buildWaitConfig(gen),
      })
        .then((descriptor) => commit(descriptor))
        .catch((err) => {
          if (committed || stale() || fallbackCtrl.signal.aborted) return;
          committed = true;
          this.closeArming();
          this.fail(
            err instanceof PlaybackError ? err : new PlaybackError('INTERNAL', 0, String(err)),
          );
        });
    };

    const resolveOnce = (): void => {
      if (committed || stale()) return;
      resolvePlaybackOnce(this.resolveOptions())
        .then((d) => commit(d))
        .catch((err) => {
          if (committed || stale()) return;
          // Not live yet (425/503/transient). We only get here because SSE
          // PUSHED live/gone, so playback lagging the push is a consistency
          // race, not "keep waiting for the next push": a healthy SSE never
          // re-pushes an unchanged state, and its heartbeats keep deferring
          // the staleness fallback — returning here wedged the post-ended
          // rejoin until the SSE lifetime-cap reconnect replayed the snapshot
          // (observed live 2026-08-25 on a 10s-delayed stream, where the
          // recreated session's /v1/playback view lagged the bridge's `live`
          // flip by ~5s). Hand the wait to the polling ladder instead: it
          // retries with backoff and commits through the same idempotent
          // commit(), and a terminal answer still surfaces through its catch.
          if (err instanceof PlaybackError && isWaitable(err.code)) {
            startFallback();
            return;
          }
          // Terminal (e.g. 410 GONE — cancelled before go-live): surface it.
          committed = true;
          this.closeArming();
          fallbackCtrl.abort();
          this.fail(
            err instanceof PlaybackError ? err : new PlaybackError('INTERNAL', 0, String(err)),
          );
        });
    };

    const rearmStale = (ms: number): void => {
      if (this.armSseTimer) clearTimeout(this.armSseTimer);
      this.armSseTimer = setTimeout(() => {
        if (!committed && !stale()) startFallback(); // SSE didn't connect / went silent
      }, ms);
    };
    rearmStale(SSE_ARM_CONNECT_GRACE_MS);

    this.armingSub = subscribeLiveState({
      eventsBaseUrl,
      matchUrn: this.opts.matchUrn,
      credential: this.opts.credential,
      onAlive: () => rearmStale(SSE_ARM_LIVENESS_MS),
      onState: (ev) => {
        if (committed || stale()) return;
        // live → go play; gone → resolve (will 410) so the terminal surfaces.
        if (ev.state === 'live' || ev.state === 'gone') {
          setTimeout(resolveOnce, Math.random() * SSE_ARM_RESOLVE_JITTER_MS);
        }
      },
      onError: () => startFallback(),
    });
  }

  private closeArming(): void {
    if (this.armSseTimer) {
      clearTimeout(this.armSseTimer);
      this.armSseTimer = null;
    }
    // Cancel the SSE arm's polling fallback too — reload()/retry()/destroy()
    // all route through here, and an orphaned poll would keep hitting
    // /v1/playback and emitting stale 'waiting' for a dead generation.
    this.armFallbackCtrl?.abort();
    this.armFallbackCtrl = null;
    this.armingSub?.close();
    this.armingSub = null;
  }

  private buildWaitConfig(gen: number): WaitForLive {
    const base: WaitForLiveOptions =
      this.opts.waitForLive && this.opts.waitForLive !== true ? this.opts.waitForLive : {};
    return {
      ...base,
      onState: (s) => {
        // Generation guard: an orphaned poll (superseded by reload/destroy)
        // must not flip a newer generation's state back to 'waiting'.
        if (this.destroyed || gen !== this.loadGen) return;
        this.setState('waiting');
        this.emit('waiting', s);
        base.onState?.(s);
      },
    };
  }

  /**
   * Build and attach the engine for a resolved descriptor. `gen` is the load
   * generation this attempt belongs to: it is re-checked after every await so a
   * destroy()/reload() that lands mid-flight cannot leave an orphaned engine or
   * timers behind (the continuation cleans up its locals and returns).
   */
  private async onResolved(descriptor: StreamDescriptor, gen: number): Promise<void> {
    if (this.destroyed || gen !== this.loadGen) return;
    this._descriptor = descriptor;
    this.endedHandled = false; // fresh playback can end again
    this._endedReason = null;

    const credential = await resolveCredential(this.opts.credential);
    if (this.destroyed || gen !== this.loadGen) return; // superseded during credential mint
    this.setState('loading');

    // Autoplay off and no play() yet: attach a deferLoad engine — it parses
    // the manifest ('ready', track lists) but fetches no media and mints no
    // DRM license until the first play(). Without this the pre-play player
    // buffered the live stream forever: state never left 'loading' (spinner,
    // no poster card) and hls.js force-seeked the paused video to the live
    // edge every ~12s (the drift ceiling), repainting the frame (AV-202).
    const deferLoad = this.opts.autoplay === false && !this.playRequested;
    const engine = this.createEngine(descriptor, credential, deferLoad);
    const detachMedia = attachMediaStateListeners(this.opts.video, (s) => {
      // Terminal latch: after 'ended'/'error' the media element stays attached
      // and its queued events — the 'pause' from engine.stop(), buffer-drain
      // 'waiting'/'stalled' after a fatal error — would flip the state right
      // back off the terminal value (a host rendering an overlay from
      // statechange sees it dismiss itself). Explicit transitions
      // (retry/reload/load → 'loading', destroy → 'idle') call setState
      // directly and are unaffected.
      if (this._state === 'ended' || this._state === 'error') return;
      this.setState(s);
    });
    await engine.load();
    if (this.destroyed || gen !== this.loadGen) {
      // Torn down or superseded while loading — release what we just built.
      detachMedia();
      engine.destroy();
      return;
    }
    if (this._state === 'error') {
      // A FATAL engine error was emitted during load() (e.g. the platform
      // fail-fast: DRM required but no key system usable here). Do not wire
      // the dead engine — no stats ticker, no 8-minute license-refresh
      // resolves, no SSE subscription. start() additionally turns this into
      // a createPlayer rejection: the emit fired before the caller could
      // attach listeners, so the event alone is unobservable on first load.
      detachMedia();
      engine.destroy();
      return;
    }

    this.engine = engine;
    this.detachMedia = detachMedia;
    if (this.opts.maxBitrate != null) engine.setMaxBitrate(this.opts.maxBitrate);
    this.startStats();
    this.scheduleLicenseRefresh();
    this.startLiveState(gen);
    this.startAnalytics(descriptor, engine, gen);
    if (deferLoad) {
      // Pre-play: 'paused' is what renders the poster + play card (and hides
      // the spinner — 'loading' means work is happening, and none is). No
      // media event will fire to move the state until play() starts loading.
      this.pendingStartLoad = true;
      this.setState('paused');
      // Native engine: its 'ready' (loadedmetadata) cannot fire pre-play
      // under preload="none", and the engine-side emit is suppressed for a
      // deferred instance — emit it here instead, so host event timing
      // matches the hls.js engine (ready from MANIFEST_PARSED, which still
      // loads). Deferred a MACROTASK: this runs synchronously inside
      // start()/reload(), before createPlayer resolves — a synchronous emit
      // would fire before the host can attach listeners and before the
      // analytics session exists to stamp noteReady(). Same pattern as the
      // SSE arm's 'waiting' emit.
      if (engine.name === 'native') {
        setTimeout(() => {
          if (this.destroyed || gen !== this.loadGen || this.engine !== engine) return;
          this.analyticsSession?.noteReady();
          this.emit('ready');
        }, 0);
      }
    }
  }

  /**
   * Start the QoE beacon session for a committed engine (plan A2). This is
   * deliberately AFTER engine selection: player identity is only truthful
   * once the engine is chosen (the SessionStart contract). A reload/retry
   * resolves a NEW sid, so the superseded session ends as STOPPED first.
   */
  /**
   * The beacon session ended itself (END_REASON_IDLE). Stop the engine with
   * it: a paused player has nothing to keep loading, and a player that
   * slept through the idle limit while playing would otherwise carry on
   * unbeaconed, miles behind live. stop() pauses the element, so the viewer
   * sees a paused player and the next play() rejoins (see idleClosed). Only
   * for the engine the session belonged to — a reload that raced the timer
   * has already replaced both.
   */
  private onAnalyticsIdle(engine: PlaybackEngine): void {
    if (this.destroyed || this.engine !== engine) return;
    this.idleClosed = true;
    this.pendingStartLoad = false; // a deferred engine is stopped too; play() reloads instead
    engine.stop();
  }

  private startAnalytics(descriptor: StreamDescriptor, engine: PlaybackEngine, gen: number): void {
    this.analyticsSession?.noteDestroyed();
    this.analyticsSession = null;
    this.detachMediaListeners?.();

    if (this.opts.analytics === false) return;
    const sid = descriptor.analytics?.sid;
    if (!sid) return; // server-side analytics flag is off — nothing to do
    const explicit =
      typeof this.opts.analytics === 'object' ? this.opts.analytics.endpoint : undefined;
    const { endpoint, disabledReason } = resolveBeaconsEndpoint(this.opts.baseUrl, explicit);
    if (!endpoint) {
      // The server minted a sid — analytics is ON for this client — but we have
      // nowhere to put the beacons. resolveBeaconsEndpoint has already said so
      // on the console; mirror it on the 'warning' event so an integrator can
      // alert on it without reading a browser console. Deferred a MACROTASK for
      // the same reason the native pre-play 'ready' is: startAnalytics runs
      // synchronously inside start(), before createPlayer resolves, so a
      // synchronous emit would fire before the host can attach listeners —
      // unobservable on exactly the first load that matters.
      if (disabledReason) {
        setTimeout(() => {
          if (this.destroyed || gen !== this.loadGen) return;
          this.emit('warning', new PlaybackError('INTERNAL', 0, disabledReason));
        }, 0);
      }
      return;
    }

    const video = this.opts.video;
    const session = new AnalyticsSession({
      endpoint,
      sid,
      player: engine.name === 'native' ? 'PLAYER_SAFARI_NATIVE' : 'PLAYER_HLSJS',
      sdkVersion: SDK_VERSION,
      environment: collectEnvironment(video),
      idleMs: typeof this.opts.analytics === 'object' ? this.opts.analytics.idleMs : undefined,
      onIdle: () => this.onAnalyticsIdle(engine),
      sample: () => {
        const stats = this.engine?.getStats() ?? EMPTY_STATS;
        const buffered = video.buffered.length
          ? Math.max(0, video.buffered.end(video.buffered.length - 1) - video.currentTime)
          : 0;
        return {
          ...stats,
          bufferedMs: Math.round(buffered * 1000),
          state: this._state,
          positionMs: Math.round(video.currentTime * 1000),
          playbackRate: video.playbackRate,
        };
      },
    });
    this.analyticsSession = session;
    const onSeeking = () => session.noteSeek();
    const onVolume = () => session.noteVolume(video.muted, video.volume);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('volumechange', onVolume);
    this.detachMediaListeners = () => {
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('volumechange', onVolume);
    };
    session.start();
    // Seeds the muted/active-watch accounting for a player that starts muted
    // (the autoplay-friendly default), which fires no volumechange of its
    // own. seedVolume, not noteVolume: the starting state is not something
    // the viewer did, and counting it would put a phantom change on every
    // session.
    session.seedVolume(video.muted, video.volume);
    session.notePresentation(this.currentPresentation());
    // Model and OS version resolve asynchronously; SessionStart is held
    // briefly for exactly this, and a browser without client hints simply
    // never patches anything.
    void collectHighEntropy().then((extra) => session.noteEnvironment(extra));
    // Autoplay disabled is a verdict this session already has: nothing will
    // be attempted, so say so rather than leaving the field unset.
    if (this.opts.autoplay === false) session.noteAutoplay('AUTOPLAY_RESULT_NOT_ATTEMPTED');
  }

  /** Which surface the video is showing on right now. */
  private currentPresentation(): Presentation {
    const video = this.opts.video as HTMLVideoElement & {
      webkitDisplayingFullscreen?: boolean;
    };
    if (typeof document !== 'undefined' && document.pictureInPictureElement === video) return 'pip';
    const fs = typeof document !== 'undefined' ? document.fullscreenElement : null;
    if (video.webkitDisplayingFullscreen === true) return 'fullscreen';
    if (fs && (fs === video || fs.contains(video))) return 'fullscreen';
    return 'inline';
  }

  private createEngine(
    descriptor: StreamDescriptor,
    credential: Credential,
    deferLoad = false,
  ): PlaybackEngine {
    const hlsConfig: Partial<HlsConfig> = { ...this.opts.hlsConfig };
    if (this.opts.startLevel != null) hlsConfig.startLevel = this.opts.startLevel;
    // liveLatencyTarget -> hls.js liveSyncDuration. The engine's normalizeLiveSync
    // derives the matching drift ceiling (liveMaxLatencyDuration, kept strictly
    // above the target) on the final merged config, so we set only the target
    // here and never risk an illegal pair. When unset, the engine default target
    // (2s) applies. liveCatchUpRate is no longer a public knob — the engine's
    // 1.5x catch-up + that ceiling handle drift; advanced callers can still set
    // maxLiveSyncPlaybackRate (or the raw live-sync keys) via hlsConfig.
    if (this.opts.liveLatencyTarget != null)
      hlsConfig.liveSyncDuration = this.opts.liveLatencyTarget;

    const deps = {
      video: this.opts.video,
      descriptor,
      credential,
      deviceId: getDeviceId(),
      userId: this.opts.userId,
      lowLatency:
        this.opts.lowLatency === undefined || this.opts.lowLatency === 'auto'
          ? true
          : this.opts.lowLatency,
      snapToLiveOnRefocus: this.opts.snapToLiveOnRefocus,
      debug: this.opts.debug,
      deferLoad,
      hlsConfig,
      emit: (ev: EngineEvent) => this.onEngineEvent(ev),
    };
    const choice = this.opts.engine ?? 'auto';
    if (choice === 'native') return new NativeHlsEngine(deps);
    if (choice === 'hls') return new HlsEngine(deps);
    // auto: FairPlay content on an Apple browser goes to the NATIVE engine.
    // hls.js/MSE cannot decrypt FairPlay here — WebKit binds the key through
    // the fragment `sinf` box, so a session built from a synthesized skd asset
    // ID reports 'usable' and still renders nothing. Measured on macOS Safari
    // 26.5 against live DRM (2026-08-06): hls.js+MSE 1 pass / 6, native 4 / 4.
    // Everything else keeps hls.js, which gives Widevine + LL-HLS control.
    //
    // webkitMediaStack() is what makes "on an Apple browser" true: Chromium
    // answers canPlayType('application/vnd.apple.mpegurl') with "maybe" while
    // being unable to play HLS natively, so nativeHlsSupported() alone sent
    // every DRM-enabled Chrome session into the native engine and failed it.
    const fairplayOnApple =
      descriptor.drmEnabled &&
      Boolean(descriptor.drm?.fairplay?.licenseUrl && descriptor.drm.fairplay.certificateUrl) &&
      nativeHlsSupported(this.opts.video) &&
      webkitMediaStack();
    if (fairplayOnApple) return new NativeHlsEngine(deps);
    if (hlsSupported()) return new HlsEngine(deps);
    if (nativeHlsSupported(this.opts.video)) return new NativeHlsEngine(deps);
    throw new PlaybackError('INTERNAL', 0, 'no supported HLS playback engine in this browser');
  }

  private onEngineEvent(ev: EngineEvent): void {
    if (this.destroyed) return;
    switch (ev.type) {
      case 'ready':
        this.analyticsSession?.noteReady();
        this.emit('ready');
        // playRequested keeps autoplay-off resume semantics: once the viewer
        // pressed play, a re-attach (rejoin after an interruption, a play()
        // during the armed wait, reload) resumes without a second gesture —
        // otherwise it would sit in 'loading' forever, exactly AV-202 again.
        if (this.opts.autoplay !== false || this.playRequested) void this.play();
        break;
      case 'lowLatencyDetected':
        if (this._descriptor) this._descriptor.isLowLatency = ev.value;
        break;
      case 'qualitychange':
        this.emit('qualitychange', ev.index);
        break;
      case 'audiotrackchange':
        this.emit('audiotrackchange', ev.id);
        break;
      case 'texttrackchange':
        this.emit('texttrackchange', ev.id);
        break;
      case 'ended':
        // HLS-layer end signal (ENDLIST, or the staleness watchdog catching a
        // frozen Tencent manifest). The SSE live-state path can reach the same
        // handler first; endedHandled dedupes.
        this.onStreamEnded();
        break;
      case 'error':
        this.analyticsSession?.noteError(
          ev.error.code,
          ev.error.message,
          ev.fatal,
          ev.error.httpStatus ?? 0,
          Math.round(this.opts.video.currentTime * 1000),
          ev.detail,
        );
        if (ev.fatal) this.fail(ev.error);
        else this.emit('warning', ev.error);
        break;
    }
  }

  /**
   * Start the push-based live-state subscription for the committed playback.
   * On `ended`/`gone` it routes to onStreamEnded — the fast, clean end signal
   * (fires when the bridge flips, before the manifest would even 503). Failures
   * are non-fatal: the subscription retries in the background and the HLS
   * staleness watchdog (engine 'ended') is the layer-fallback, so a missing or
   * unreachable events endpoint never affects playback. But a PERSISTENTLY
   * failing subscription (blocked origin, wrong events host, CSP) used to be
   * fully invisible — end detection silently degraded to the watchdog with no
   * way to tell from the outside. After LIVE_STATE_WARN_AFTER consecutive
   * failures with no data ever flowing, a one-time non-fatal 'warning' is
   * emitted so integrators can see it; a later successful connect resets the
   * counter (and re-arms the warning) since the outage evidently healed.
   */
  private startLiveState(gen: number): void {
    if (this.opts.liveStateEvents === false) return;
    const base = this.opts.eventsBaseUrl ?? deriveEventsBaseUrl(this.opts.baseUrl);
    if (!base) return;
    this.closeLiveState();
    let failures = 0;
    let warned = false;
    this.liveStateSub = subscribeLiveState({
      eventsBaseUrl: base,
      matchUrn: this.opts.matchUrn,
      credential: this.opts.credential,
      onState: (ev) => {
        if (this.destroyed || gen !== this.loadGen) return;
        if (ev.state === 'gone') {
          // Past the catch-up window — truly terminal, nothing left to rejoin.
          this.onStreamEnded(true, ev.reason ?? 'match_ended');
        } else if (ev.state === 'ended') {
          this.onStreamEnded(false, ev.reason);
        } else if (ev.state === 'live' && this.endedHandled) {
          // A live push AFTER end-of-stream means the bridge recreated the
          // session (encoder reconnected past the grace window) — and it now
          // serves under a NEW manifest URL, so only a re-resolve gets back.
          this.scheduleRejoin(gen);
        }
      },
      onAlive: () => {
        // Data flowed — the subscription is healthy. Reset so only a NEW
        // sustained outage (not routine reconnects) can trigger the warning.
        failures = 0;
        warned = false;
      },
      onError: () => {
        if (this.destroyed || gen !== this.loadGen || warned) return;
        // After end-of-stream the subscription's only remaining job is the
        // rejoin trigger — end detection already happened, so the degraded-
        // detection warning below would be misinformation there.
        if (this.endedHandled) return;
        failures += 1;
        if (failures >= LIVE_STATE_WARN_AFTER) {
          warned = true;
          this.emit(
            'warning',
            new PlaybackError(
              'NETWORK',
              0,
              `live-state events unreachable at ${base} after ${failures} attempts — ` +
                `end-of-stream detection degraded to the HLS staleness watchdog. ` +
                `Check the events host, the api-key's allowed origins, and CSP connect-src.`,
            ),
          );
        }
      },
    });
  }

  private closeLiveState(): void {
    this.liveStateSub?.close();
    this.liveStateSub = null;
  }

  /**
   * End-of-stream: stop the engine's buffer/poll loop (keeping the last
   * frame) and surface `ended` (via setState). Idempotent across the SSE and
   * HLS-watchdog signals. The live-state subscription closes only when the
   * end is `terminal` (`gone` — past the catch-up window) or rejoin is off;
   * otherwise it stays open as the rejoin trigger — a later pushed `live`
   * means the session was recreated and playback can re-arm (scheduleRejoin).
   */
  private onStreamEnded(terminal = false, reason?: EndedReason): void {
    if (this.destroyed) return;
    if (terminal || !this.rejoinEnabled()) {
      this.cancelRejoin();
      this.closeLiveState();
    }
    if (this.endedHandled) {
      // Already ended, but the SERVER may have refined WHY: an outage that
      // outlived the bridge's hold reports `interrupted` first and flips to
      // `match_ended` when the match actually finishes (havik-streams #191
      // publishes that as a second event — the state never changes, only the
      // reason). Upgrade the reason and re-render so a viewer stops being
      // told "reconnecting" for a match that is over. Never downgrade: once
      // we know the match ended, a late `interrupted` cannot un-end it.
      if (reason === 'match_ended' && this._endedReason !== 'match_ended') {
        this._endedReason = reason;
        // Only `ended` — NOT `statechange`. The state does not transition
        // here (it was, and stays, 'ended'), and `statechange` is documented
        // as a transition signal: re-emitting it would make an
        // edge-triggered host run its end-of-stream work twice. `ended`
        // re-firing with a refined reason IS part of its contract (see
        // PlayerEventMap), and the built-in control bar re-renders off it.
        this.emit('ended', reason);
      }
      return;
    }
    this.endedHandled = true;
    this._endedReason = reason ?? null;
    this.stopRefresh();
    this.pendingStartLoad = false; // the engine is stopped; a later play() must not re-kick it
    this.engine?.stop();
    this.setState('ended');
  }

  private rejoinEnabled(): boolean {
    return this.opts.rejoinOnLive !== false && this.opts.liveStateEvents !== false;
  }

  /**
   * A `live` push arrived after end-of-stream. Jittered like the armed
   * go-live resolve, so a popular stream's stranded viewers don't all
   * re-resolve in lockstep when the encoder comes back.
   */
  private scheduleRejoin(gen: number): void {
    if (!this.rejoinEnabled() || this.rejoinTimer) return;
    this.rejoinTimer = setTimeout(() => {
      this.rejoinTimer = null;
      if (this.destroyed || gen !== this.loadGen || this._state !== 'ended') return;
      this.rejoin();
    }, Math.random() * SSE_ARM_RESOLVE_JITTER_MS);
  }

  private cancelRejoin(): void {
    if (this.rejoinTimer) {
      clearTimeout(this.rejoinTimer);
      this.rejoinTimer = null;
    }
  }

  /**
   * Tear down the ended playback and re-enter the armed wait under a fresh
   * load generation. beginArming (SSE-first) re-resolves off the events
   * snapshot / `live` push, which keeps this robust to the recreate race: if
   * the pushed `live` slightly precedes /v1/playback consistency, the arming
   * ladder keeps waiting through waitable errors instead of failing the
   * player, and a terminal answer (`gone` → 410) still surfaces as an error.
   */
  private rejoin(): void {
    const gen = ++this.loadGen; // supersedes anything stale for the old playback
    this.teardownPlayback();
    this.setState('waiting');
    this.beginArming(gen, undefined, true);
  }

  /**
   * Stop and release the committed-playback plumbing: stats, license refresh,
   * any pending rejoin, arming, the live-state subscription, media listeners,
   * and the engine. Callers bump loadGen first so in-flight continuations for
   * the old playback go stale. destroy() keeps its own sequence — it
   * interleaves these with controls/PiP/analytics teardown in a load-bearing
   * order.
   */
  private teardownPlayback(): void {
    this.stopStats();
    this.stopRefresh();
    this.cancelRejoin();
    this.closeArming();
    this.closeLiveState();
    this.detachMedia?.();
    this.engine?.destroy();
    this.engine = null;
    this.pendingStartLoad = false; // the deferred engine is gone with it
    this.idleClosed = false; // and so is the idle-closed session; the next engine starts clean
  }

  /**
   * Set up the chosen controls. 'custom' (default) wraps the <video> in a
   * `.havik-player` container, applies the (themeable) Oddin skin, and mounts
   * the control bar. 'native' shows the browser UI; 'none' shows nothing.
   */
  private mountControls(): void {
    const mode = this.opts.controls ?? 'custom';
    const video = this.opts.video;
    if (mode === 'native') {
      video.controls = true;
      return;
    }
    if (mode === 'none') {
      video.controls = false;
      return;
    }
    video.controls = false;
    const doc = video.ownerDocument ?? document;
    const container = doc.createElement('div');
    const parent = video.parentNode;
    if (parent) parent.insertBefore(container, video);
    container.appendChild(video);
    applyTheme(container, this.opts.theme);
    this.container = container;
    this.controlBar = mountControlBar({
      root: container,
      video,
      player: this,
      logoUrl: this.opts.theme?.logoUrl,
      statusOverlays: this.opts.statusOverlays,
      endedMessage: this.opts.endedMessage,
      interruptedMessage: this.opts.interruptedMessage,
      errorMessage: this.opts.errorMessage,
    });
  }

  private unmountControls(): void {
    this.controlBar?.destroy();
    this.controlBar = null;
    const container = this.container;
    if (container) {
      if (container.parentNode) {
        container.parentNode.insertBefore(this.opts.video, container);
        container.remove();
      } else if (this.opts.video.parentNode === container) {
        // Mounted while the video was still detached (host creates the
        // element before inserting it): there is no parent to restore into,
        // but the video must still be released from the wrapper — otherwise
        // a destroyed player (or a rejected createPlayer, where the caller
        // never gets a handle to destroy) leaves an orphan container holding
        // the element.
        container.removeChild(this.opts.video);
      }
    }
    this.container = null;
  }

  private attachPipListeners(): void {
    const v = this.opts.video;
    const sync = () => this.analyticsSession?.notePresentation(this.currentPresentation());
    const onEnter = () => {
      sync();
      this.emit('pipchange', true);
    };
    const onLeave = () => {
      sync();
      this.emit('pipchange', false);
    };
    v.addEventListener('enterpictureinpicture', onEnter);
    v.addEventListener('leavepictureinpicture', onLeave);
    // Fullscreen is a document-level event and the QoE plane wants the same
    // answer for both surfaces, so one sync serves them: how long a viewer
    // watched fullscreen, in PiP, or inline.
    const doc = typeof document !== 'undefined' ? document : undefined;
    doc?.addEventListener('fullscreenchange', sync);
    doc?.addEventListener('webkitfullscreenchange', sync);
    this.detachPip = () => {
      v.removeEventListener('enterpictureinpicture', onEnter);
      v.removeEventListener('leavepictureinpicture', onLeave);
      doc?.removeEventListener('fullscreenchange', sync);
      doc?.removeEventListener('webkitfullscreenchange', sync);
    };
  }

  private startStats(): void {
    this.stopStats();
    const interval = Math.max(500, this.opts.statsIntervalMs ?? DEFAULT_STATS_INTERVAL_MS);
    this.statsTimer = setInterval(() => {
      if (!this.engine) return;
      const stats = this.engine.getStats();
      // First-segment startup stage at stats granularity (~2s), not the 15s
      // beacon cadence — first_frag_load_ms would otherwise overreport badly.
      if ((stats.bytesLoaded ?? 0) > 0) this.analyticsSession?.noteFirstBytes();
      this.emit('stats', stats);
    }, interval);
  }

  private stopStats(): void {
    if (this.statsTimer) clearInterval(this.statsTimer);
    this.statsTimer = undefined;
  }

  private scheduleLicenseRefresh(): void {
    this.stopRefresh();
    const interval = this.opts.licenseRefreshMs ?? DEFAULT_LICENSE_REFRESH_MS;
    if (!interval || !this._descriptor?.drmEnabled) return;
    this.refreshTimer = setInterval(() => void this.refreshLicense(), interval);
  }

  private stopRefresh(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = undefined;
  }

  private async refreshLicense(): Promise<void> {
    const engine = this.engine;
    if (this.destroyed || !engine) return;
    try {
      const fresh = await resolvePlaybackOnce(this.resolveOptions());
      // A reload()/destroy() may have swapped or dropped the engine while the
      // refresh GET was in flight — don't apply a stale URL to a newer engine.
      if (this.destroyed || this.engine !== engine) return;
      const widevine = fresh.drm?.widevine?.licenseUrl;
      const fairplay = fresh.drm?.fairplay?.licenseUrl;
      if (widevine || fairplay) {
        engine.setLicenseUrls({ widevine, fairplay });
        this._descriptor = fresh;
      }
    } catch (err) {
      // A terminal error (e.g. 410 ended) means there's nothing left to refresh;
      // surface it as a non-fatal warning so a stalling refresh is observable.
      if (err instanceof PlaybackError && !isWaitable(err.code)) {
        this.stopRefresh();
        if (!this.destroyed) this.emit('warning', err);
      }
    }
  }

  async play(): Promise<void> {
    if (this.destroyed) return;
    const firstRequest = !this.playRequested;
    this.playRequested = true;
    // After an idle close the session and the engine are gone: rejoin. With
    // playRequested set, reload() → 'ready' re-enters play() on the fresh
    // engine, where the ordinary path below runs.
    // Not from a terminal state: an `ended` push that landed after the idle
    // close owns the UI exactly as it does below, and a reload would resolve
    // a finished match into an error card.
    if (this.idleClosed && this._state !== 'ended' && this._state !== 'error') {
      this.idleClosed = false;
      await this.reload();
      return;
    }
    // Terminal states own the UI; a play() arriving there (the control-bar
    // button stays clickable on the ended card) must not kick the loader and
    // resurrect 'loading' over an ended/error stream — the pre-PR player
    // resumed buffered frames there, and a dead session has nothing to load.
    const kickedDeferred =
      this.pendingStartLoad && this._state !== 'ended' && this._state !== 'error';
    // Startup is measured from HERE for the autoplay-off path, not from
    // load(): the player has been sitting behind a poster with the manifest
    // already parsed, and the time the viewer spent deciding is theirs.
    //
    // Gated on the PLAYER being autoplay-off, not on kickedDeferred, and
    // that difference is load-bearing. A gestureless play() that the policy
    // blocks still clears pendingStartLoad, and the blocked path
    // deliberately leaves the loader warm rather than re-latching — so the
    // eventual real gesture does NOT take the kicked branch. Anchoring there
    // would have pinned startup to the failed probe and measured the whole
    // wait for the gesture as latency, which is the bug this is here to fix.
    // notePlayIntent's own guard stops a post-playback resume re-anchoring a
    // startup that already finished; before the first frame, the LAST intent
    // is the right one. The blocked branch below says the same thing in
    // words: "a policy-blocked play() is not viewer intent".
    //
    // autoplay:true never reaches this: its intent is at load(), where the
    // manifest fetch genuinely is part of startup, so its ladder keeps
    // measuring from bornMs.
    if (this.opts.autoplay === false) this.analyticsSession?.notePlayIntent();
    if (kickedDeferred) {
      // First play() on an autoplay-off player: start media loading now.
      // 'loading' is honest again from here — the spinner shows while the
      // first segments and (when enabled) the DRM license are fetched, until
      // the media element's own events take over.
      this.pendingStartLoad = false;
      this.setState('loading');
      this.engine?.startLoad();
    }
    try {
      await this.opts.video.play();
      // The autoplay verdict, which only exists once play() has resolved.
      // Reported on the FIRST attempt of a session; SessionStart has gone by
      // the time a later user gesture plays, so a hand-started session can
      // never overwrite it.
      if (firstRequest) {
        this.analyticsSession?.noteAutoplay(
          this.opts.video.muted ? 'AUTOPLAY_RESULT_ALLOWED_MUTED' : 'AUTOPLAY_RESULT_ALLOWED',
        );
      }
    } catch (err) {
      // Autoplay blocked by browser policy → emit 'autoplayblocked' so the host
      // can show a tap-to-play / tap-to-unmute affordance. Other rejections
      // (e.g. a competing-load AbortError) are benign; genuine playback failures
      // arrive via the 'error' event from the engine.
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        this.analyticsSession?.noteAutoplay('AUTOPLAY_RESULT_BLOCKED');
        // A policy-blocked play() is not viewer intent: nothing ever played.
        // Un-latch, so a later reload/rejoin does not keep attempting
        // gestureless auto-plays — but only when THIS call set the latch; a
        // block after real playback must keep resume semantics.
        if (firstRequest) this.playRequested = false;
        // And put the pre-play card back instead of the spinner the blocked
        // kick left up. The loader is not stopped HERE: the warmed buffer
        // makes a prompt gesture play() start instantly; the hls.js engine's
        // own pause suspension stops it after its grace (PAUSE_SUSPEND_MS)
        // so a play card nobody clicks does not pull the live stream forever.
        // The state guard keeps a mid-await teardown/reload/ended transition
        // untouched.
        if (kickedDeferred && !this.destroyed && this._state === 'loading') {
          this.setState('paused');
        }
        this.emit('autoplayblocked');
      }
    }
  }

  pause(): void {
    if (this.destroyed) return;
    this.opts.video.pause();
  }
  setMuted(muted: boolean): void {
    if (this.destroyed) return;
    this.opts.video.muted = muted;
  }
  setVolume(volume: number): void {
    if (this.destroyed) return;
    this.opts.video.volume = Math.min(1, Math.max(0, volume));
  }

  getStats(): PlaybackStats {
    return this.engine?.getStats() ?? EMPTY_STATS;
  }

  getQualityLevels(): QualityLevel[] {
    return this.engine?.getQualityLevels() ?? [];
  }
  getCurrentQuality(): number {
    return this.engine?.getCurrentQuality() ?? -1;
  }
  setQuality(index: number | 'auto'): void {
    if (this.destroyed) return;
    this.engine?.setQuality(index === 'auto' ? -1 : index);
  }
  setMaxBitrate(bitrate: number | null): void {
    if (this.destroyed) return;
    this.engine?.setMaxBitrate(bitrate);
  }
  getAudioTracks(): AudioTrackInfo[] {
    return this.engine?.getAudioTracks() ?? [];
  }
  setAudioTrack(id: number): void {
    if (this.destroyed) return;
    this.engine?.setAudioTrack(id);
  }
  getTextTracks(): TextTrackInfo[] {
    return this.engine?.getTextTracks() ?? [];
  }
  setTextTrack(id: number): void {
    if (this.destroyed) return;
    this.engine?.setTextTrack(id);
  }
  seekToLive(): void {
    if (this.destroyed) return;
    this.engine?.seekToLive();
  }
  async enterPictureInPicture(): Promise<void> {
    if (this.destroyed) return;
    const v = this.opts.video as HTMLVideoElement & {
      requestPictureInPicture?: () => Promise<unknown>;
    };
    if (typeof v.requestPictureInPicture === 'function') await v.requestPictureInPicture();
  }
  async exitPictureInPicture(): Promise<void> {
    const doc = document as Document & {
      exitPictureInPicture?: () => Promise<void>;
      pictureInPictureElement?: Element | null;
    };
    if (
      doc.pictureInPictureElement === this.opts.video &&
      typeof doc.exitPictureInPicture === 'function'
    ) {
      await doc.exitPictureInPicture();
    }
  }
  async enterFullscreen(): Promise<boolean> {
    if (this.destroyed) return false;
    // The custom-controls wrapper (when mounted) is the element-fullscreen
    // target so the branded UI stays visible; native/none modes present the
    // bare video, where the browser's own fullscreen controls appear.
    return enterVideoFullscreen(this.opts.video, this.container ?? undefined);
  }
  async exitFullscreen(): Promise<void> {
    await exitVideoFullscreen(this.opts.video, this.container ?? undefined);
  }
  retry(): Promise<void> {
    return this.reload();
  }

  async reload(): Promise<void> {
    if (this.destroyed) return;
    const gen = ++this.loadGen; // supersedes any in-flight load/arming
    this.teardownPlayback();
    try {
      const descriptor = await resolveStream({
        ...this.resolveOptions(),
        waitForLive: this.opts.waitForLive ? this.buildWaitConfig(gen) : undefined,
      });
      await this.onResolved(descriptor, gen);
      if (gen === this.loadGen && this._state === 'error' && this._lastError) {
        // Fatal engine load — already routed through fail() by the engine
        // event channel; reject so the awaiting caller observes it too.
        throw this._lastError;
      }
    } catch (err) {
      if (this.destroyed || this.abort.signal.aborted || gen !== this.loadGen) return;
      // Route a failed reload through the error channel (consistent with start/arming)
      // AND reject so an awaiting caller observes it — unless it already went
      // through fail() above (double-emitting 'error' would fire host handlers twice).
      if (err !== this._lastError) {
        this.fail(
          err instanceof PlaybackError ? err : new PlaybackError('INTERNAL', 0, String(err)),
        );
      }
      throw err;
    }
  }

  on<E extends PlayerEvent>(event: E, cb: (payload: PlayerEventMap[E]) => void): () => void {
    const set = (this.listeners[event] ??= new Set()) as Set<(p: PlayerEventMap[E]) => void>;
    set.add(cb);
    return () => set.delete(cb);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.loadGen += 1; // invalidate any in-flight load/arming continuation
    this.abort.abort();
    this.stopStats();
    this.stopRefresh();
    // Tear the control bar down (and unsubscribe its listeners) before the final
    // 'idle' emit so it never touches a half-dismantled DOM.
    this.unmountControls();
    this.cancelRejoin();
    this.closeArming();
    this.closeLiveState();
    this.detachMedia?.();
    this.detachPip?.();
    // End the beacon session BEFORE the engine dies so the final sample can
    // still read totals; no-op if 'ended'/fatal already closed it.
    this.analyticsSession?.noteDestroyed();
    this.analyticsSession = null;
    this.detachMediaListeners?.();
    this.engine?.destroy();
    this.engine = null;
    this.setState('idle'); // final terminal signal to any still-registered listeners
    this.clearListeners();
  }

  private clearListeners(): void {
    for (const key of Object.keys(this.listeners)) {
      (this.listeners as Record<string, Set<unknown> | undefined>)[key]?.clear();
    }
  }

  private fail(err: PlaybackError): void {
    this._lastError = err;
    this.setState('error');
    // After the terminal StateChange: SessionEnd(FATAL_ERROR) closes the
    // beacon session — further events for this sid are meaningless.
    this.analyticsSession?.noteFatal();
    this.emit('error', err);
  }

  private setState(state: PlayerState): void {
    if (state === this._state) return;
    const from = this._state;
    this._state = state;
    if (this.analyticsSession) {
      const video = this.opts.video;
      this.analyticsSession.noteStateChange(
        from,
        state,
        Math.round(video.currentTime * 1000),
        this.engine?.getStats().levelHeight,
      );
      if (state === 'ended') this.analyticsSession.noteEnded();
    }
    this.emit('statechange', state);
    switch (state) {
      case 'playing':
        this.emit('playing');
        break;
      case 'buffering':
        this.emit('buffering');
        break;
      case 'paused':
        this.emit('paused');
        break;
      case 'ended':
        this.emit('ended', this._endedReason ?? undefined);
        break;
      default:
        break;
    }
  }

  private emit<E extends PlayerEvent>(event: E, payload?: PlayerEventMap[E]): void {
    const set = this.listeners[event] as
      Set<(p: PlayerEventMap[E] | undefined) => void> | undefined;
    set?.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // never let a listener break the player
      }
    });
  }
}
